import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { Config } from '../../common/config';
import LeftMenu from '../../components/leftMenu';
import useAxiosFetch from '../../hooks/useAxiosFetch';
import useStoreNo from '../../hooks/useStoreNo';
import { sanitizeNoticeHtml } from '../../utils/sanitizeNoticeHtml';
import './noticeManage.css';

/**
 * [2026-10-27] 상점 공지 관리 — 상점이 쓴 공지 목록 · 작성 · 수정 · 사용 중지 · 다시 사용.
 *  백엔드: naegift-api-promotion src/notice/admin-notice.controller.ts (admin/notice/mine·POST·PUT·DELETE·enable)
 *  작성처(author_type=store, author_no=로그인 상점)·작성자(admin_no)는 서버가 정한다.
 *  노출 대상은 마켓(앱의 내 상점 화면)만 — 상점주 본인에게 상점관리자 공지를 올릴 일이 없어서 뺐다(10/27).
 *  이벤트 공지는 내 상점이 발행한 프로모션만.
 *  입력 항목은 operator NoticeManage.jsx 와 같다(팝업 · 필수 동의 · HTML 직접 입력 · 버튼 링크 — 10/27 대표님 결정).
 */

const PAGE_SIZE = 10;

const QUILL_TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline'],
  [{ color: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link', 'image'],
  ['clean'],
];
// ★"image" 가 빠지면 insertEmbed 로 넣은 이미지도 조용히 버려진다(operator 와 같음)
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'color', 'list', 'link', 'image'];

const NOTICE_IMAGE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';
const NOTICE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
// 서버 DTO(NOTICE_BUTTON_URL_REGEX)와 같은 규칙 — https:// 전체 주소만
const BUTTON_URL_RE = /^https:\/\/[^\s\\/?#]+([/?#][^\s\\]*)?$/;
const NOTICE_BODY_MAX_BYTES = 95000;

const Delta = ReactQuill.Quill.import('delta');
// 붙여넣은 이미지 중 https 가 아닌 것(data: base64 등)은 버린다 — 앱 정화 함수가 https 이미지만 보여 준다
function dropNonHttpsImage(node, delta) {
  const src = node.getAttribute('src') || '';
  return /^https:\/\//i.test(src) ? delta : new Delta();
}

const NOTICE_KIND = { NORMAL: 'NORMAL', COUPON_EVENT: 'COUPON_EVENT', ROULETTE_EVENT: 'ROULETTE_EVENT' };
const NOTICE_KIND_LABEL = {
  [NOTICE_KIND.NORMAL]: '일반',
  [NOTICE_KIND.COUPON_EVENT]: '쿠폰이벤트',
  [NOTICE_KIND.ROULETTE_EVENT]: '룰렛이벤트',
};
const KIND_PROMOTION_TYPE = {
  [NOTICE_KIND.COUPON_EVENT]: 'COUPON',
  [NOTICE_KIND.ROULETTE_EVENT]: 'GAME',
};
// [2026-10-27] 상점 공지는 마켓 대상만 — 선택 없이 항상 market 으로 보낸다
const STORE_NOTICE_TARGET = 'market';

const EMPTY_FORM = {
  noticeKind: NOTICE_KIND.NORMAL,
  promotionNo: '',
  consentRequired: 'N',
  marketingOnly: 'N',
  title: '',
  content: '',
  contentFormat: 'quill',
  htmlContent: '',
  startDttm: '',
  endDttm: '',
  popupYn: 'N',
  buttonText: '',
  buttonUrl: '',
};

const linkedNoOf = row => row.promotionNo ?? row.linkedPromotion?.promotionNo ?? null;

function isBlankContent(html) {
  if (!html) return true;
  if (/<img\b/i.test(html)) return false;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length === 0;
}

// timestamptz → YYYY-MM-DD (KST). end_dttm 은 그날 KST 23:59:59 라 로컬 시각으로 뽑으면 하루 밀릴 수 있다.
function toDateInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const kst = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
  const pad = n => String(n).padStart(2, '0');
  return `${kst.getFullYear()}-${pad(kst.getMonth() + 1)}-${pad(kst.getDate())}`;
}

/** [2026-11-04] 이벤트 공지 노출기간 = 연결 프로모션 기간(서버도 저장 시 프로모션 기간으로 덮어쓴다). 시작일 없으면 오늘부터 */
function promoNoticePeriod(p) {
  return { startDttm: toDateInput(p?.startDttm) || toDateInput(new Date()), endDttm: toDateInput(p?.endDttm) };
}

function errorMessageOf(res, fallback) {
  let msg = res?.data?.message ?? res?.data?.resultMessage;
  if (Array.isArray(msg)) msg = msg.join(' ');
  if (/request entity too large/i.test(msg || '')) {
    return '본문이 너무 큽니다. <style>·<head>·주석을 지우거나 공지를 나눠 주세요.';
  }
  return msg || fallback;
}

const isOk = res => res && res.status >= 200 && res.status < 300;

export default function NoticeManage() {
  const storeNo = useStoreNo();
  // [2026-11-02 N-09] 직원은 끝까지 쓴 뒤 저장에서 거절되던 문제 — 주소로 들어와도 화면에서 먼저 막는다
  const userInfo = useSelector((state) => state.userInfo);
  const isOwner =
    userInfo?.role === 'AG001' ||
    (Array.isArray(userInfo?.roles) && userInfo.roles.includes('admin'));
  const isStaff = !!(userInfo?.role || userInfo?.roles) && !isOwner;
  const { fetchData } = useAxiosFetch();
  const buttonUrlExample = `${(Config.SHOPUSER_SERVER || 'https://shopuser-dev.naegift.com/').replace(/\/+$/, '')}/${storeNo || '{상점번호}'}/notice`;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [pageNo, setPageNo] = useState(1);
  const [loading, setLoading] = useState(true);

  const [mode, setMode] = useState(null); // null(목록) | 'add' | 'edit'
  const [editingRow, setEditingRow] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [promoOptions, setPromoOptions] = useState([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [htmlView, setHtmlView] = useState('code');
  const quillRef = useRef(null);
  const htmlTextareaRef = useRef(null);

  const loadList = useCallback(
    async (page = 1) => {
      setLoading(true);
      const res = await fetchData({
        url: `${Config.API_SERVER}notice/mine?pageNo=${page}&pageSize=${PAGE_SIZE}`,
        method: 'get',
      });
      const data = res?.data?.data;
      setRows(isOk(res) ? data?.list ?? [] : []);
      setTotal(isOk(res) ? data?.total ?? 0 : 0);
      setTotalPage(isOk(res) ? Math.max(data?.totalPage ?? 1, 1) : 1);
      setLoading(false);
    },
    [fetchData]
  );

  useEffect(() => {
    loadList(pageNo);
  }, [loadList, pageNo]);

  // 이벤트 공지면 연결할 진행 중 프로모션 — 내 상점이 발행한 것만(서버도 저장 때 다시 확인)
  useEffect(() => {
    const promotionType = KIND_PROMOTION_TYPE[form.noticeKind];
    if (!mode || !promotionType) {
      setPromoOptions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setPromoLoading(true);
      const res = await fetchData({
        url: `${Config.API_SERVER}promotion?promotionType=${promotionType}&status=ACTIVE&pageNo=1&pageSize=100`,
        method: 'get',
      });
      const list = isOk(res) ? res?.data?.data?.list ?? [] : [];
      // 쿠폰은 목록형(코드 없음)만 쿠폰받기 대상, 꽝 보상 쿠폰은 쿠폰받기에 안 나와 연결 불가(operator 와 같음)
      const filtered = list.filter(
        p =>
          p.issuerType === 'STORE' &&
          (promotionType !== 'COUPON' || (!p.couponCode && !p.missRewardLinked))
      );
      if (!cancelled) {
        setPromoOptions(filtered);
        setPromoLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, form.noticeKind, fetchData]);

  // [2026-11-04] 이벤트 공지는 노출기간을 연결 프로모션 기간으로 보여준다 — 수정 화면에서도 예전 저장값 대신
  //  (후보 목록에 없으면 공지 응답의 linkedPromotion 기간). 서버도 저장 시 같은 값으로 덮어쓴다.
  useEffect(() => {
    if (form.noticeKind === NOTICE_KIND.NORMAL || !form.promotionNo) return;
    const p =
      promoOptions.find((o) => String(o.promotionNo) === form.promotionNo) ||
      (editingRow && String(linkedNoOf(editingRow)) === form.promotionNo ? editingRow.linkedPromotion : null);
    if (!p) return;
    const next = promoNoticePeriod(p);
    setForm((f) =>
      f.startDttm === next.startDttm && f.endDttm === next.endDttm ? f : { ...f, ...next },
    );
  }, [form.noticeKind, form.promotionNo, promoOptions, editingRow]);

  const uploadImage = useCallback(
    async file => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetchData({
        url: `${Config.API_SERVER}notice/images`,
        method: 'post',
        data: fd,
      });
      if (!isOk(res)) throw new Error(errorMessageOf(res, '이미지 업로드에 실패했습니다.'));
      const filePath = res?.data?.data?.filePath;
      if (!filePath) throw new Error('업로드 응답에 이미지 주소가 없습니다.');
      return filePath;
    },
    [fetchData]
  );

  const pickNoticeImage = useCallback(
    onUploaded => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = NOTICE_IMAGE_ACCEPT;
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        if (!NOTICE_IMAGE_ACCEPT.split(',').includes(file.type)) {
          alert('이미지는 jpg·png·gif·webp 파일만 올릴 수 있습니다.');
          return;
        }
        if (file.size > NOTICE_IMAGE_MAX_BYTES) {
          alert('이미지는 5MB 이하만 올릴 수 있습니다.');
          return;
        }
        setImageUploading(true);
        try {
          const url = await uploadImage(file);
          if (!/^https:\/\//i.test(url)) {
            alert('이미지 주소가 https가 아니라 공지 화면에 보이지 않습니다.');
            return;
          }
          onUploaded(url);
        } catch (e) {
          alert(e.message);
        } finally {
          setImageUploading(false);
        }
      };
      input.click();
    },
    [uploadImage]
  );

  const handleImageButton = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true);
    pickNoticeImage(url => {
      const index = range ? range.index : editor.getLength();
      editor.insertEmbed(index, 'image', url, 'user');
      editor.setSelection(index + 1, 0, 'user');
    });
  }, [pickNoticeImage]);

  const handleHtmlImageButton = () => {
    const ta = htmlTextareaRef.current;
    const start = ta ? ta.selectionStart : null;
    const end = ta ? ta.selectionEnd : null;
    pickNoticeImage(url => {
      const tag = `<img src="${url.replace(/"/g, '&quot;')}" alt="">`;
      setForm(f => {
        const text = f.htmlContent;
        const from = start == null ? text.length : Math.min(start, text.length);
        const to = end == null ? from : Math.min(Math.max(end, from), text.length);
        return { ...f, htmlContent: text.slice(0, from) + tag + text.slice(to) };
      });
    });
  };

  // ★modules 는 한 번만 만든다 — 객체가 바뀌면 에디터를 새로 만들어 커서가 날아간다
  const quillModules = useMemo(
    () => ({
      toolbar: { container: QUILL_TOOLBAR, handlers: { image: handleImageButton } },
      clipboard: { matchers: [['IMG', dropNonHttpsImage]] },
    }),
    [handleImageButton]
  );

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditingRow(null);
    setHtmlView('code');
    setMode('add');
  };

  const openEdit = row => {
    const isHtml = row.contentFormat === 'html';
    setForm({
      noticeKind: row.noticeKind || NOTICE_KIND.NORMAL,
      promotionNo: linkedNoOf(row) != null ? String(linkedNoOf(row)) : '',
      consentRequired: row.consentRequired === 'Y' ? 'Y' : 'N',
      marketingOnly: row.marketingOnly === 'Y' ? 'Y' : 'N',
      title: row.title || '',
      content: isHtml ? '' : row.content || '',
      contentFormat: isHtml ? 'html' : 'quill',
      htmlContent: isHtml ? row.content || '' : '',
      startDttm: toDateInput(row.startDttm),
      endDttm: toDateInput(row.endDttm),
      popupYn: row.popupYn === 'Y' ? 'Y' : 'N',
      buttonText: row.buttonText ?? '',
      buttonUrl: row.buttonUrl ?? '',
    });
    setEditingRow(row);
    setHtmlView('code');
    setMode('edit');
  };

  const closeForm = () => {
    if (saving || imageUploading) return;
    if (!window.confirm('저장하지 않은 내용이 사라집니다. 목록으로 돌아갈까요?')) return;
    setMode(null);
  };

  // HTML 로 저장된 공지는 에디터로 되돌릴 수 없다(표·div 가 지워짐) — operator 와 같은 규칙
  const formatLocked = mode === 'edit' && editingRow?.contentFormat === 'html';

  const handleFormatChange = next => {
    if (next === form.contentFormat) return;
    if (next === 'quill' && formatLocked) return;
    if (
      next === 'html' &&
      mode === 'edit' &&
      editingRow?.contentFormat !== 'html' &&
      !window.confirm('HTML 직접 입력으로 저장하면 이 공지는 에디터로 되돌릴 수 없습니다. 바꿀까요?')
    ) {
      return;
    }
    setForm(f => ({
      ...f,
      contentFormat: next,
      htmlContent:
        next === 'html' && !f.htmlContent.trim() && !isBlankContent(f.content) ? f.content : f.htmlContent,
    }));
    setHtmlView('code');
  };

  const htmlPreview = useMemo(
    () => (form.contentFormat === 'html' ? sanitizeNoticeHtml(form.htmlContent, 'html') : ''),
    [form.contentFormat, form.htmlContent]
  );

  const validate = () => {
    if (form.noticeKind !== NOTICE_KIND.NORMAL && !form.promotionNo) return '연결할 프로모션을 선택하세요.';
    const title = form.title.trim();
    if (!title) return '제목을 입력하세요.';
    if (title.length > 200) return '제목은 200자를 넘을 수 없습니다.';
    if (form.contentFormat === 'html') {
      if (isBlankContent(sanitizeNoticeHtml(form.htmlContent, 'html'))) {
        return '화면에 보일 본문이 없습니다. HTML을 확인해 주세요.';
      }
    } else if (isBlankContent(form.content)) {
      return '본문을 입력하세요.';
    }
    if (!form.startDttm) return '노출 시작일을 입력하세요.';
    if (form.endDttm && form.endDttm < form.startDttm) return '노출 종료일은 시작일보다 앞설 수 없습니다.';
    const buttonText = form.buttonText.trim();
    const buttonUrl = form.buttonUrl.trim();
    if (!!buttonText !== !!buttonUrl) return '버튼 문구와 링크는 함께 입력해야 합니다.';
    if (buttonText.length > 30) return '버튼 문구는 30자를 넘을 수 없습니다.';
    if (buttonUrl && (buttonUrl.length > 512 || !BUTTON_URL_RE.test(buttonUrl))) {
      return `버튼 링크는 전체 주소만 넣어주세요. 예) ${buttonUrlExample}`;
    }
    return null;
  };

  const handleSubmit = async () => {
    const message = validate();
    if (message) {
      alert(message);
      return;
    }
    // 서버 ValidationPipe 가 DTO 밖 필드를 거절하므로 필요한 칸만 보낸다. 날짜는 YYYY-MM-DD 그대로(KST 파싱)
    const body = {
      targetType: STORE_NOTICE_TARGET,
      consentRequired: form.consentRequired,
      marketingOnly: form.marketingOnly,
      title: form.title.trim(),
      content: form.contentFormat === 'html' ? form.htmlContent : form.content,
      contentFormat: form.contentFormat,
      startDttm: form.startDttm,
      popupYn: form.popupYn,
      buttonText: form.buttonText.trim(),
      buttonUrl: form.buttonUrl.trim(),
      noticeKind: form.noticeKind,
      promotionNo: form.noticeKind !== NOTICE_KIND.NORMAL ? Number(form.promotionNo) : null,
    };
    if (form.endDttm) body.endDttm = form.endDttm;
    if (new Blob([JSON.stringify(body)]).size > NOTICE_BODY_MAX_BYTES) {
      alert('본문이 너무 큽니다. <style>·<head>·주석을 지우거나 공지를 나눠 주세요.');
      return;
    }

    setSaving(true);
    const res = await fetchData(
      mode === 'add'
        ? { url: `${Config.API_SERVER}notice`, method: 'post', data: body }
        : { url: `${Config.API_SERVER}notice/${editingRow.noticeNo}`, method: 'put', data: body }
    );
    setSaving(false);
    if (!isOk(res)) {
      alert(errorMessageOf(res, '저장에 실패했습니다.'));
      return;
    }
    setMode(null);
    loadList(mode === 'add' ? 1 : pageNo);
    if (mode === 'add') setPageNo(1);
  };

  const toggleUse = async row => {
    const stop = row.useYn !== 'N';
    if (!window.confirm(stop ? `"${row.title}" 공지를 사용 중지할까요?` : `"${row.title}" 공지를 다시 사용할까요?`)) return;
    const res = await fetchData(
      stop
        ? { url: `${Config.API_SERVER}notice/${row.noticeNo}`, method: 'delete' }
        : { url: `${Config.API_SERVER}notice/${row.noticeNo}/enable`, method: 'post' }
    );
    if (!isOk(res)) {
      alert(errorMessageOf(res, '처리에 실패했습니다.'));
      return;
    }
    loadList(pageNo);
  };

  const locked = saving;

  return (
    <>
      <section className="location_box location_box_mypage">
        <ul className="location_001">
          <li>
            <NavLink to={storeNo ? `/${storeNo}/gift-list` : '/gift-list'}>홈</NavLink>
          </li>
          <li>공지 관리</li>
        </ul>
      </section>

      <main>
        <section className="contants_box contants_box_mypage">
          <LeftMenu pageName="noticeManage" />

          <section className="rightcontant_box notice-manage">
            {isStaff ? (
              <p style={{ padding: '60px 0', textAlign: 'center', color: '#666' }}>
                공지 관리는 사장님 계정만 사용할 수 있습니다.
              </p>
            ) : mode === null ? (
              <>
                <div className="notice-manage__head">
                  <h2 className="tit_01" style={{ margin: 0 }}>공지 관리</h2>
                  <button type="button" className="button_04 button_red" onClick={openAdd}>
                    + 공지 작성
                  </button>
                </div>
                <p className="notice-manage__desc">
                  내 상점 공지를 작성합니다. 작성한 공지는 앱의 내 상점 화면(상점정보 &gt; 상점 공지사항, 상점 홈)에 보입니다.
                </p>

                {/* [2026-10-28] 상점 정보 관리처럼 테두리 카드로 */}
                <article className="form_card list_card">
                <table className="table_box_01">
                  <colgroup>
                    <col width="110px" />
                    <col />
                    <col width="80px" />
                    <col width="200px" />
                    <col width="120px" />
                    <col width="200px" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>종류</th>
                      <th style={{ textAlign: 'left' }}>제목</th>
                      <th>팝업</th>
                      <th>노출 기간</th>
                      <th>작성 / 수정</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center' }}>불러오는 중...</td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center' }}>작성한 공지가 없습니다.</td>
                      </tr>
                    ) : (
                      rows.map(row => {
                        const lp = row.linkedPromotion;
                        const no = linkedNoOf(row);
                        return (
                          <tr key={row.noticeNo} className={row.useYn === 'N' ? 'notice-manage__stopped' : ''}>
                            <td>
                              {/* [2026-10-28] 종류는 색 배지로 — 좁은 칸에서 "쿠폰이벤/트"로 깨지던 것 */}
                              <span className={`notice-manage__kind notice-manage__kind--${row.noticeKind || NOTICE_KIND.NORMAL}`}>
                                {NOTICE_KIND_LABEL[row.noticeKind] || '일반'}
                              </span>
                            </td>
                            <td style={{ textAlign: 'left' }}>
                              {row.title}
                              {row.useYn === 'N' && <span className="notice-manage__badge">사용 중지</span>}
                              {row.noticeKind && row.noticeKind !== NOTICE_KIND.NORMAL && no != null && (
                                <span className="notice-manage__sub">
                                  연결: #{no} {lp?.name || ''}
                                </span>
                              )}
                            </td>
                            <td>
                              {row.popupYn === 'Y' ? <span className="notice-manage__popup">노출</span> : '-'}
                            </td>
                            <td>
                              {toDateInput(row.startDttm) || '-'} ~ {row.endDttm ? toDateInput(row.endDttm) : '무기한'}
                            </td>
                            <td>
                              {row.writerName || '-'}
                              {row.updaterName && row.updaterName !== row.writerName && (
                                <span className="notice-manage__sub">수정: {row.updaterName}</span>
                              )}
                            </td>
                            <td className="notice-manage__actions">
                              <button
                                type="button"
                                className="button_04 button_gray_line"
                                onClick={() => openEdit(row)}
                              >
                                수정
                              </button>{' '}
                              <button
                                type="button"
                                className="button_04 button_gray_line"
                                onClick={() => toggleUse(row)}
                              >
                                {row.useYn === 'N' ? '다시 사용' : '사용 중지'}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
                <div className="notice-manage__pager">
                  <span>전체 {total}건</span>
                  <button
                    type="button"
                    className="button_04 button_gray_line"
                    disabled={pageNo <= 1}
                    onClick={() => setPageNo(p => p - 1)}
                  >
                    이전
                  </button>
                  <span>
                    {pageNo} / {totalPage}
                  </span>
                  <button
                    type="button"
                    className="button_04 button_gray_line"
                    disabled={pageNo >= totalPage}
                    onClick={() => setPageNo(p => p + 1)}
                  >
                    다음
                  </button>
                </div>
                </article>
              </>
            ) : (
              <>
                <h2 className="tit_01">{mode === 'add' ? '공지 작성' : '공지 수정'}</h2>
                {mode === 'edit' && editingRow?.writerName && (
                  <p className="notice-manage__desc">
                    작성: {editingRow.writerName}
                    {editingRow.updaterName ? ` · 최근 수정: ${editingRow.updaterName}` : ''}
                  </p>
                )}

                <div className="notice-form form_card">
                  <div className="notice-form__row">
                    <label className="notice-form__label">공지 종류</label>
                    <select
                      className="input_01 select_01"
                      value={form.noticeKind}
                      disabled={locked}
                      onChange={e => {
                        const noticeKind = e.target.value;
                        setForm(f => ({
                          ...f,
                          noticeKind,
                          promotionNo: '',
                        }));
                      }}
                    >
                      <option value={NOTICE_KIND.NORMAL}>일반 공지</option>
                      <option value={NOTICE_KIND.COUPON_EVENT}>쿠폰이벤트 — [쿠폰 받으러 가기] 버튼</option>
                      <option value={NOTICE_KIND.ROULETTE_EVENT}>룰렛이벤트 — [룰렛 하러 가기] 버튼</option>
                    </select>
                  </div>

                  {form.noticeKind !== NOTICE_KIND.NORMAL && (
                    <div className="notice-form__row">
                      <label className="notice-form__label">연결할 프로모션</label>
                      <div>
                        <select
                          className="input_01 select_01"
                          value={form.promotionNo}
                          disabled={locked || promoLoading}
                          onChange={e => {
                            const promotionNo = e.target.value;
                            const picked = promoOptions.find(p => String(p.promotionNo) === promotionNo);
                            setForm(f => ({ ...f, promotionNo, ...(picked ? promoNoticePeriod(picked) : {}) }));
                          }}
                        >
                          <option value="">
                            {promoLoading
                              ? '불러오는 중...'
                              : promoOptions.length
                                ? '선택하세요'
                                : '진행 중인 내 상점 프로모션이 없습니다'}
                          </option>
                          {form.promotionNo &&
                            !promoOptions.some(p => String(p.promotionNo) === form.promotionNo) && (
                              <option value={form.promotionNo}>
                                #{form.promotionNo} {editingRow?.linkedPromotion?.name || ''} (진행 중 아님)
                              </option>
                            )}
                          {promoOptions.map(p => (
                            <option key={p.promotionNo} value={String(p.promotionNo)}>
                              #{p.promotionNo} {p.name}
                              {p.endDttm ? ` · ~${toDateInput(p.endDttm)}` : ''}
                            </option>
                          ))}
                        </select>
                        <div className="notice-form__help">
                          내 상점이 발행한 프로모션만 연결할 수 있습니다. 앱 공지에서 버튼을 누르면{' '}
                          {form.noticeKind === NOTICE_KIND.COUPON_EVENT ? '쿠폰받기 화면으로' : '룰렛 화면으로'} 이동합니다.
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="notice-form__row">
                    <label className="notice-form__label">노출 대상</label>
                    <div className="notice-form__static">
                      마켓 — 앱의 내 상점 화면(상점정보 &gt; 상점 공지사항, 상점 홈 팝업)
                    </div>
                  </div>

                  <div className="notice-form__row">
                    <label className="notice-form__label">제목</label>
                    <input
                      className="input_01"
                      value={form.title}
                      maxLength={200}
                      disabled={locked}
                      placeholder="공지 제목 (200자 이내)"
                      onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    />
                  </div>

                  <div className="notice-form__row">
                    <label className="notice-form__label">노출 옵션</label>
                    <div className="notice-form__checks">
                      <label>
                        <input
                          type="checkbox"
                          checked={form.popupYn === 'Y'}
                          disabled={locked}
                          onChange={e => {
                            const checked = e.target.checked;
                            setForm(f => ({
                              ...f,
                              popupYn: checked ? 'Y' : 'N',
                              ...(checked ? {} : { consentRequired: 'N', marketingOnly: 'N' }),
                            }));
                          }}
                        />{' '}
                        팝업으로 띄우기
                      </label>
                      <label className="notice-form__sub-check">
                        <input
                          type="checkbox"
                          checked={form.consentRequired === 'Y'}
                          disabled={locked || form.popupYn !== 'Y'}
                          onChange={e => setForm(f => ({ ...f, consentRequired: e.target.checked ? 'Y' : 'N' }))}
                        />{' '}
                        필수 동의 공지
                      </label>
                      <label className="notice-form__sub-check">
                        <input
                          type="checkbox"
                          checked={form.marketingOnly === 'Y'}
                          disabled={locked || form.popupYn !== 'Y'}
                          onChange={e => setForm(f => ({ ...f, marketingOnly: e.target.checked ? 'Y' : 'N' }))}
                        />{' '}
                        마케팅 수신 동의자에게만
                      </label>
                      <div className="notice-form__help">팝업을 켜야 하위 옵션을 설정할 수 있습니다.</div>
                    </div>
                  </div>

                  <div className="notice-form__row">
                    <label className="notice-form__label">노출 기간</label>
                    <div>
                      <input
                        type="date"
                        className="input_01"
                        value={form.startDttm}
                        disabled={locked || form.noticeKind !== NOTICE_KIND.NORMAL}
                        onChange={e => setForm(f => ({ ...f, startDttm: e.target.value }))}
                      />{' '}
                      ~{' '}
                      <input
                        type="date"
                        className="input_01"
                        value={form.endDttm}
                        disabled={locked || form.noticeKind !== NOTICE_KIND.NORMAL}
                        onChange={e => setForm(f => ({ ...f, endDttm: e.target.value }))}
                      />
                      <div className="notice-form__help">
                        {form.noticeKind !== NOTICE_KIND.NORMAL
                          ? '이벤트 공지는 연결한 프로모션 기간 동안만 노출됩니다(프로모션 기간을 바꾸면 공지도 같이 바뀝니다).'
                          : '시작일 00:00부터 종료일 23:59(KST)까지. 종료일을 비우면 무기한입니다.'}
                      </div>
                    </div>
                  </div>

                  <div className="notice-form__row">
                    <label className="notice-form__label">본문</label>
                    <div>
                      <div className="notice-form__checks notice-form__inline">
                        <label>
                          <input
                            type="radio"
                            name="contentFormat"
                            checked={form.contentFormat === 'quill'}
                            disabled={locked || imageUploading || formatLocked}
                            onChange={() => handleFormatChange('quill')}
                          />{' '}
                          에디터
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="contentFormat"
                            checked={form.contentFormat === 'html'}
                            disabled={locked || imageUploading}
                            onChange={() => handleFormatChange('html')}
                          />{' '}
                          HTML 직접 입력
                        </label>
                      </div>
                      {formatLocked && (
                        <div className="notice-form__help">HTML 공지는 에디터로 바꿀 수 없습니다. HTML 코드에서 고쳐 주세요.</div>
                      )}
                      {form.contentFormat === 'html' ? (
                        <>
                          <div className="notice-form__toolbar">
                            <button
                              type="button"
                              className={`button_04 ${htmlView === 'code' ? 'button_gray' : 'button_gray_line'}`}
                              onClick={() => setHtmlView('code')}
                            >
                              코드
                            </button>
                            <button
                              type="button"
                              className={`button_04 ${htmlView === 'preview' ? 'button_gray' : 'button_gray_line'}`}
                              onClick={() => setHtmlView('preview')}
                            >
                              미리보기
                            </button>
                            <button
                              type="button"
                              className="button_04 button_gray_line"
                              disabled={locked || imageUploading}
                              onClick={handleHtmlImageButton}
                            >
                              {imageUploading ? '올리는 중...' : '이미지 올리기'}
                            </button>
                          </div>
                          {htmlView === 'code' ? (
                            <textarea
                              ref={htmlTextareaRef}
                              className="notice-form__code"
                              value={form.htmlContent}
                              readOnly={locked}
                              spellCheck={false}
                              placeholder="다른 곳에서 만든 HTML을 붙여 넣으세요."
                              onChange={e => setForm(f => ({ ...f, htmlContent: e.target.value }))}
                            />
                          ) : (
                            // 앱과 같은 정화 결과만 렌더한다
                            <div className="notice-form__preview" dangerouslySetInnerHTML={{ __html: htmlPreview }} />
                          )}
                          <div className="notice-form__help">
                            &lt;style&gt;·class 서식, 스크립트·폼·동영상은 적용되지 않습니다. 이미지는 https 주소만 보이고, 폭은 600px 이하를 권장합니다.
                          </div>
                        </>
                      ) : (
                        <>
                          <ReactQuill
                            theme="snow"
                            ref={quillRef}
                            value={form.content}
                            modules={quillModules}
                            formats={QUILL_FORMATS}
                            readOnly={locked}
                            placeholder="공지 본문을 입력하세요."
                            onChange={html => setForm(f => ({ ...f, content: html }))}
                          />
                          <div className="notice-form__help">
                            {imageUploading
                              ? '이미지를 올리는 중입니다...'
                              : '이미지는 5MB 이하 jpg·png·gif·webp 파일을 이미지 버튼으로 올려 주세요(붙여넣은 이미지는 빠집니다).'}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="notice-form__row">
                    <label className="notice-form__label">버튼 (선택)</label>
                    <div>
                      <input
                        className="input_01"
                        value={form.buttonText}
                        maxLength={30}
                        disabled={locked}
                        placeholder="버튼 문구 예) 자세히 보기"
                        onChange={e => setForm(f => ({ ...f, buttonText: e.target.value }))}
                      />{' '}
                      <input
                        className="input_01 notice-form__url"
                        value={form.buttonUrl}
                        maxLength={512}
                        disabled={locked}
                        placeholder={buttonUrlExample}
                        onChange={e => setForm(f => ({ ...f, buttonUrl: e.target.value }))}
                      />
                      <div className="notice-form__help">
                        문구와 링크(https 전체 주소)를 모두 넣으면 공지 상세 하단에 버튼이 보입니다. 팝업에는 보이지 않습니다.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="notice-form__actions">
                  <button type="button" className="button_04 button_gray_line" disabled={saving} onClick={closeForm}>
                    취소
                  </button>
                  <button
                    type="button"
                    className="button_04 button_red"
                    disabled={saving || imageUploading}
                    onClick={handleSubmit}
                  >
                    {saving ? '저장 중...' : '저장'}
                  </button>
                </div>
              </>
            )}
          </section>
        </section>
      </main>
    </>
  );
}
